<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TeamsAPI from 'dashboard/api/teams';

const { t } = useI18n();
const profiles = computed(() => [
  {
    id: 'cofae',
    title: t('AGENT_MGMT.AGENT_TYPES.COFAE'),
    scope: t('AGENT_MGMT.PERMISSIONS_GUIDE.COFAE.SCOPE'),
    can: t('AGENT_MGMT.PERMISSIONS_GUIDE.COFAE.CAN'),
    cannot: t('AGENT_MGMT.PERMISSIONS_GUIDE.COFAE.CANNOT'),
  },
  {
    id: 'customer_service',
    title: t('AGENT_MGMT.AGENT_TYPES.CUSTOMER_SERVICE'),
    scope: t('AGENT_MGMT.PERMISSIONS_GUIDE.CUSTOMER_SERVICE.SCOPE'),
    can: t('AGENT_MGMT.PERMISSIONS_GUIDE.CUSTOMER_SERVICE.CAN'),
    cannot: t('AGENT_MGMT.PERMISSIONS_GUIDE.CUSTOMER_SERVICE.CANNOT'),
  },
  {
    id: 'agent',
    title: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    scope: t('AGENT_MGMT.PERMISSIONS_GUIDE.AGENT.SCOPE'),
    can: t('AGENT_MGMT.PERMISSIONS_GUIDE.AGENT.CAN'),
    cannot: t('AGENT_MGMT.PERMISSIONS_GUIDE.AGENT.CANNOT'),
  },
  {
    id: 'controller',
    title: t('AGENT_MGMT.AGENT_TYPES.CONTROLLER'),
    scope: t('AGENT_MGMT.PERMISSIONS_GUIDE.CONTROLLER.SCOPE'),
    can: t('AGENT_MGMT.PERMISSIONS_GUIDE.CONTROLLER.CAN'),
    cannot: t('AGENT_MGMT.PERMISSIONS_GUIDE.CONTROLLER.CANNOT'),
  },
  {
    id: 'administrator',
    title: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    scope: t('AGENT_MGMT.PERMISSIONS_GUIDE.ADMINISTRATOR.SCOPE'),
    can: t('AGENT_MGMT.PERMISSIONS_GUIDE.ADMINISTRATOR.CAN'),
    cannot: t('AGENT_MGMT.PERMISSIONS_GUIDE.ADMINISTRATOR.CANNOT'),
  },
]);

const teams = ref([]);
const teamsState = ref('idle');
const membersByTeam = ref({});
const memberState = ref({});

const memberProfileLabel = member => {
  if (member.role === 'administrator') {
    return t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR');
  }
  if (member.agent_profile === 'cofae') {
    return t('AGENT_MGMT.AGENT_TYPES.COFAE');
  }
  if (member.agent_profile === 'customer_service') {
    return t('AGENT_MGMT.AGENT_TYPES.CUSTOMER_SERVICE');
  }
  if (member.agent_profile === 'controller') {
    return t('AGENT_MGMT.AGENT_TYPES.CONTROLLER');
  }
  if (member.custom_role_id) {
    return t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.CUSTOM_ROLE');
  }
  return t('AGENT_MGMT.AGENT_TYPES.AGENT');
};

const loadTeams = async () => {
  teamsState.value = 'loading';
  try {
    const { data } = await TeamsAPI.get(true);
    teams.value = Array.isArray(data) ? data : [];
    teamsState.value = 'ready';
  } catch {
    teamsState.value = 'error';
  }
};

const ensureTeamsLoaded = () => {
  if (teamsState.value === 'idle') loadTeams();
};

const loadMembers = async teamId => {
  if (['loading', 'ready'].includes(memberState.value[teamId])) return;

  memberState.value = { ...memberState.value, [teamId]: 'loading' };
  try {
    const { data } = await TeamsAPI.getAgents({ teamId });
    membersByTeam.value = {
      ...membersByTeam.value,
      [teamId]: Array.isArray(data) ? data : [],
    };
    memberState.value = { ...memberState.value, [teamId]: 'ready' };
  } catch {
    memberState.value = { ...memberState.value, [teamId]: 'error' };
  }
};
</script>

<template>
  <details
    class="group mb-5 rounded-xl border border-n-weak bg-n-background"
    @toggle="$event.target.open && ensureTeamsLoaded()"
  >
    <summary
      class="flex cursor-pointer list-none items-center justify-between gap-3 p-4 sm:p-5 [&::-webkit-details-marker]:hidden"
    >
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TITLE') }}
      </span>
      <span
        aria-hidden="true"
        class="i-lucide-chevron-down size-5 shrink-0 text-n-slate-11 transition-transform group-open:rotate-180"
      />
    </summary>

    <div class="border-t border-n-weak p-4 sm:p-5">
      <p class="mb-0 text-body-main text-n-slate-11">
        {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.INTRO') }}
      </p>

      <div class="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <article
          v-for="profile in profiles"
          :key="profile.id"
          class="rounded-xl border border-n-weak bg-n-slate-1 p-4"
        >
          <h3 class="mb-3 text-heading-3 text-n-slate-12">
            {{ profile.title }}
          </h3>
          <dl class="mb-0 flex flex-col gap-3 text-body-main">
            <div>
              <dt class="font-medium text-n-slate-12">
                {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.SCOPE') }}
              </dt>
              <dd class="m-0 text-n-slate-11">
                {{ profile.scope }}
              </dd>
            </div>
            <div>
              <dt class="font-medium text-n-slate-12">
                {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.CAN') }}
              </dt>
              <dd class="m-0 text-n-slate-11">
                {{ profile.can }}
              </dd>
            </div>
            <div>
              <dt class="font-medium text-n-slate-12">
                {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.CANNOT') }}
              </dt>
              <dd class="m-0 text-n-slate-11">
                {{ profile.cannot }}
              </dd>
            </div>
          </dl>
        </article>
      </div>

      <div class="mt-4 rounded-lg border border-n-weak bg-n-slate-2 p-4">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.TITLE') }}
        </h3>
        <p class="mb-2 text-body-main text-n-slate-11">
          {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.DESCRIPTION') }}
        </p>
        <p class="mb-2 text-body-main text-n-slate-11">
          {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.ACCESS') }}
        </p>
        <p v-if="teamsState === 'loading'" class="mb-2 text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.LOADING') }}
        </p>
        <div v-else-if="teamsState === 'error'" class="mb-2 flex gap-2">
          <span class="text-sm text-n-slate-11">
            {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.ERROR') }}
          </span>
          <button
            type="button"
            class="text-sm font-medium text-n-blue-11 hover:underline"
            @click="loadTeams"
          >
            {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.RETRY') }}
          </button>
        </div>
        <p v-else-if="!teams.length" class="mb-2 text-sm text-n-slate-11">
          {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.EMPTY') }}
        </p>
        <div v-else class="mb-3 flex flex-col gap-2">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{
              $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.COUNT', {
                count: teams.length,
              })
            }}
          </p>
          <details
            v-for="team in teams"
            :key="team.id"
            class="rounded-lg border border-n-weak bg-n-background p-3"
            @toggle="$event.target.open && loadMembers(team.id)"
          >
            <summary class="cursor-pointer font-medium text-n-slate-12">
              {{ team.name }}
            </summary>
            <div class="mt-2 flex flex-col gap-2 text-sm text-n-slate-11">
              <p v-if="team.description" class="mb-0">{{ team.description }}</p>
              <p class="mb-0">
                {{
                  team.allow_auto_assign
                    ? $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.AUTO_ASSIGN_ON')
                    : $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.AUTO_ASSIGN_OFF')
                }}
              </p>
              <p v-if="memberState[team.id] === 'loading'" class="mb-0">
                {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.MEMBERS_LOADING') }}
              </p>
              <div
                v-else-if="memberState[team.id] === 'error'"
                class="flex gap-2"
              >
                <span>{{
                  $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.MEMBERS_ERROR')
                }}</span>
                <button
                  type="button"
                  class="font-medium text-n-blue-11 hover:underline"
                  @click="loadMembers(team.id)"
                >
                  {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.RETRY') }}
                </button>
              </div>
              <div v-else-if="memberState[team.id] === 'ready'">
                <p class="mb-1 font-medium text-n-slate-12">
                  {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.MEMBERS') }}
                </p>
                <ul
                  v-if="membersByTeam[team.id]?.length"
                  class="mb-0 ps-5 list-disc"
                >
                  <li v-for="member in membersByTeam[team.id]" :key="member.id">
                    {{ member.name }}
                    <span class="text-n-slate-10">
                      {{
                        $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.MEMBER_ROLE', {
                          role: memberProfileLabel(member),
                        })
                      }}
                    </span>
                  </li>
                </ul>
                <p v-else class="mb-0">
                  {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.NO_MEMBERS') }}
                </p>
              </div>
            </div>
          </details>
        </div>
        <router-link
          :to="{
            name: 'settings_teams_list',
            params: { accountId: $route.params.accountId },
          }"
          class="inline-flex items-center gap-1 text-body-main font-medium text-n-blue-11 hover:underline"
        >
          {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.TEAMS.LINK') }}
          <span aria-hidden="true" class="i-lucide-arrow-up-right size-4" />
        </router-link>
      </div>

      <p class="mb-0 mt-4 text-body-main font-medium text-n-slate-12">
        {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.RESOLUTION_POLICY') }}
      </p>
      <p class="mb-0 mt-1 text-sm text-n-slate-11">
        {{ $t('AGENT_MGMT.PERMISSIONS_GUIDE.CUSTOM_ROLE_NOTE') }}
      </p>
    </div>
  </details>
</template>
